import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, CircleCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMessage } from "@/lib/api";
import { AuthShell, authInputCls, authLinkCls } from "@/pages/Login";

const BackToLogin = () => (
  <p className="mt-5 text-sm text-muted-foreground lg:text-slate-600">
    <Link data-testid="back-to-login-link" to="/connexion" className={authLinkCls}>Retour à la connexion</Link>
  </p>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(data.message);
    } catch (err) { setError(errMessage(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell kicker="Accès sécurisé" title="Mot de passe oublié" subtitle="Indiquez l'adresse e-mail de votre compte : nous vous enverrons un lien pour choisir un nouveau mot de passe.">
      {done ? (
        <p data-testid="forgot-success" role="status" className="flex items-start gap-2 rounded-lg border border-emerald-500/50 bg-emerald-950/60 p-3 text-sm text-emerald-200"><MailCheck className="mt-0.5 h-4 w-4 shrink-0" /> {done}</p>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="forgot-email" className="lg:font-semibold">Adresse e-mail</Label>
            <Input id="forgot-email" data-testid="forgot-email-input" type="email" autoComplete="email" placeholder="nom@entreprise.fr" className={authInputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          {error && <p data-testid="forgot-error" role="alert" className="text-sm text-red-300 lg:text-red-700">{error}</p>}
          <Button data-testid="forgot-submit-button" type="submit" disabled={busy} className="h-12 w-full text-base font-bold">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Envoyer le lien
          </Button>
        </form>
      )}
      <BackToLogin />
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState(token ? "" : "Lien incomplet : faites une nouvelle demande de réinitialisation.");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Le mot de passe doit contenir au moins 8 caractères.");
    if (password !== confirm) return setError("Les deux mots de passe ne correspondent pas.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setDone(data.message);
    } catch (err) { setError(errMessage(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell kicker="Accès sécurisé" title="Nouveau mot de passe" subtitle="Choisissez un nouveau mot de passe d'au moins 8 caractères.">
      {done ? (
        <div className="space-y-4">
          <p data-testid="reset-success" role="status" className="flex items-start gap-2 rounded-lg border border-emerald-500/50 bg-emerald-950/60 p-3 text-sm text-emerald-200"><CircleCheck className="mt-0.5 h-4 w-4 shrink-0" /> {done}</p>
          <Button asChild className="h-12 w-full text-base font-bold"><Link data-testid="reset-go-login" to="/connexion">Se connecter</Link></Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="new-password" className="lg:font-semibold">Nouveau mot de passe</Label>
            <Input id="new-password" data-testid="reset-password-input" type="password" autoComplete="new-password" className={authInputCls} value={password} onChange={(e) => setPassword(e.target.value)} required disabled={!token} />
          </div>
          <div>
            <Label htmlFor="confirm-password" className="lg:font-semibold">Confirmer le mot de passe</Label>
            <Input id="confirm-password" data-testid="reset-confirm-input" type="password" autoComplete="new-password" className={authInputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} required disabled={!token} />
          </div>
          {error && <p data-testid="reset-error" role="alert" className="text-sm text-red-300 lg:text-red-700">{error}</p>}
          <Button data-testid="reset-submit-button" type="submit" disabled={busy || !token} className="h-12 w-full text-base font-bold">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer le mot de passe
          </Button>
        </form>
      )}
      {!done && <p className="mt-5 text-sm"><Link data-testid="reset-new-request-link" to="/mot-de-passe-oublie" className={authLinkCls}>Faire une nouvelle demande</Link></p>}
      <BackToLogin />
    </AuthShell>
  );
}
